import { AlertCircle, RefreshCw, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import contractsV2Service, { ContractUsageResponse } from "../../../../services/contractsV2Service";

interface ContractUsageModalProps {
  isOpen: boolean;
  onClose: () => void;
  conId: number;
  symbol: string;
}

const ContractUsageModal: React.FC<ContractUsageModalProps> = ({ isOpen, onClose, conId, symbol }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ContractUsageResponse | null>(null);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await contractsV2Service.getContractUsage(conId);
      setData(result);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch contract usage");
    } finally {
      setLoading(false);
    }
  }, [conId]);

  useEffect(() => {
    if (isOpen) {
      fetchUsage();
    }
    return () => {
      setData(null);
      setError(null);
    };
  }, [isOpen, fetchUsage]);

  if (!isOpen) return null;

  const providerMap = new Map(data?.providers?.map((p) => [p.id, p.name]) || []);

  const thClasses = `px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
    isDarkMode ? "text-gray-400" : "text-gray-500"
  }`;
  const tdClasses = `px-4 py-2 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;
  const emptyClasses = `px-4 py-6 text-center text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} rounded-lg shadow-xl max-w-4xl w-full overflow-hidden max-h-[80vh] flex flex-col`}
      >
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? "border-rp-separator" : "border-gray-200"
          }`}
        >
          <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Usage: {symbol} ({conId})
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div
                className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
                  isDarkMode ? "border-rp-amber" : "border-blue-600"
                }`}
              />
            </div>
          )}

          {error && (
            <div
              className={`rounded-lg border p-4 ${
                isDarkMode
                  ? "bg-red-900/20 border-red-800 text-red-400"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium text-sm">Failed to load usage data</span>
              </div>
              <p className="text-sm mb-3">{error}</p>
              <button
                onClick={fetchUsage}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-rp-amber hover:bg-yellow-600 text-white transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          )}

          {data && !loading && (
            <>
              <Section title="Baskets" count={data.baskets.length} isDarkMode={isDarkMode}>
                {data.baskets.length === 0 ? (
                  <tr><td colSpan={3} className={emptyClasses}>No baskets found</td></tr>
                ) : (
                  data.baskets.map((b) => (
                    <tr key={b.id} className={isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"}>
                      <td className={`${tdClasses} font-mono`}>{b.id}</td>
                      <td className={tdClasses}>{b.title}</td>
                      <td className={tdClasses}>{providerMap.get(b.providerId) || b.providerId}</td>
                    </tr>
                  ))
                )}
              </Section>

              <Section title="Portfolios" count={data.portfolios.length} isDarkMode={isDarkMode}>
                {data.portfolios.length === 0 ? (
                  <tr><td colSpan={3} className={emptyClasses}>No portfolios found</td></tr>
                ) : (
                  data.portfolios.map((p) => (
                    <tr key={p.id} className={isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"}>
                      <td className={`${tdClasses} font-mono`}>{p.id}</td>
                      <td className={tdClasses}>{p.title}</td>
                      <td className={tdClasses}>{providerMap.get(p.providerId) || p.providerId}</td>
                    </tr>
                  ))
                )}
              </Section>

              <div>
                <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  Users ({data.users.length})
                </h3>
                <div
                  className={`border rounded-lg overflow-hidden ${
                    isDarkMode ? "border-rp-separator" : "border-gray-200"
                  }`}
                >
                  <table className="w-full">
                    <thead className={isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}>
                      <tr>
                        <th className={thClasses}>ID</th>
                        <th className={thClasses}>Name</th>
                        <th className={thClasses}>Email</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
                      {data.users.length === 0 ? (
                        <tr>
                          <td colSpan={3} className={emptyClasses}>No users found</td>
                        </tr>
                      ) : (
                        data.users.map((u) => (
                          <tr key={u.id} className={isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"}>
                            <td className={`${tdClasses} font-mono text-xs`}>{u.id}</td>
                            <td className={tdClasses}>{u.name}</td>
                            <td className={tdClasses}>{u.email}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div
          className={`flex items-center justify-end p-4 border-t ${
            isDarkMode ? "border-rp-separator" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              isDarkMode
                ? "text-gray-300 hover:text-white hover:bg-rp-surface-raised"
                : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{
  title: string;
  count: number;
  isDarkMode: boolean;
  children: React.ReactNode;
}> = ({ title, count, isDarkMode, children }) => {
  const thClasses = `px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
    isDarkMode ? "text-gray-400" : "text-gray-500"
  }`;

  return (
    <div>
      <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
        {title} ({count})
      </h3>
      <div
        className={`border rounded-lg overflow-hidden ${
          isDarkMode ? "border-rp-separator" : "border-gray-200"
        }`}
      >
        <table className="w-full">
          <thead className={isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}>
            <tr>
              <th className={thClasses}>ID</th>
              <th className={thClasses}>Title</th>
              <th className={thClasses}>Provider</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            {children}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContractUsageModal;
